// Blog/guide post content helpers, split by direction:
//
//   normalizeForEditor(html)  -> used when LOADING into / SAVING from the Quill
//      editor. Turns URL-as-text links back into PLAIN TEXT. Storing plain URLs
//      (no <a>) is the key fix: Quill mangles <a> tags whose text is a URL when
//      it re-parses HTML on the next load (splitting "https://" off, adding <p>
//      tags). With no <a> in the stored content there is nothing to mangle.
//
//   normalizeForView(html)    -> used by the read-only PostPage, which renders
//      the stored HTML DIRECTLY (no Quill). Here it is safe to make plain URL
//      text clickable, because there is no Quill round-trip to corrupt it.
//
// Descriptive links (visible text is NOT a bare URL, e.g. <a href=url>Đọc
// thêm</a>) are always preserved by both functions.

// Quill 2 (react-quill-new) xuất HTML bằng getSemanticHTML(), hàm này mã hoá
// MỌI dấu cách thành &nbsp;. Kết quả: cả đoạn văn thành một "từ" không ngắt
// được — web/app chỉ còn điểm xuống dòng ở "-", "(", ")", "/" nên dòng nhảy
// lung tung, còn đoạn không có các ký tự đó thì tràn khung và bị cắt mất chữ ở
// cuối (React Native không có word-break). Trả &nbsp; về dấu cách thường.
// Áp dụng cả 2 chiều: LƯU (không sinh thêm bài hỏng) và XEM (vá bài đã lưu).
const NBSP_RE = /&nbsp;|\u00a0/gi;

function relaxNbsp(html) {
    return typeof html === 'string' ? html.replace(NBSP_RE, ' ') : html;
}

const URL_RE = /https?:\/\/[^\s<]+/g;
// A leftover protocol prefix text node from a previously-split link.
const PROTOCOL_PREFIX_RE = /^https?:?\/?\/?$/i;
// The whole visible string is exactly one URL (no surrounding text).
const FULL_URL_RE = /^https?:\/\/[^\s]+$/i;

function parse(html) {
    if (!html || typeof html !== 'string') return null;
    if (typeof window === 'undefined' || !window.DOMParser) return null;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return { doc, body: doc.body };
    } catch (e) {
        return null;
    }
}

// True when `block` holds nothing but the given split URL (the protocol-prefix
// text node + the <a>), ignoring whitespace — i.e. a URL Quill pushed onto its
// own paragraph.
function isLoneSplitUrlBlock(block, a, protocolPrefixNode) {
    return Array.from(block.childNodes).every((child) => {
        if (child === a || child === protocolPrefixNode) return true;
        return (
            child.nodeType === Node.TEXT_NODE &&
            !(child.textContent || '').trim()
        );
    });
}

// Append `node` to the end of `prevBlock`, inserting a single space first unless
// the block already ends with whitespace.
function rejoinOntoPrevLine(prevBlock, node) {
    if (!/\s$/.test(prevBlock.textContent || '')) {
        prevBlock.appendChild(prevBlock.ownerDocument.createTextNode(' '));
    }
    prevBlock.appendChild(node);
}

// Repair URL-as-text links so Quill stops mangling them (splitting "https://"
// off, pushing the URL onto its own <p>). Handling depends on the link target:
//
//   • Display URL == its own href (or no href): store as PLAIN TEXT — Quill then
//     has no <a> to mangle, and the read-only view re-linkifies it. Stays inline.
//   • Display is a URL but href points ELSEWHERE (custom target, e.g. showing
//     longhousee.com/shopee but linking to facebook): PRESERVE the link with its
//     href; just reassemble the split prefix and rejoin the split-off paragraph.
//
// Descriptive links (visible text is NOT a bare URL, e.g. "Đọc thêm") are left
// untouched in all cases.
function unwrapUrlLinks(body) {
    body.querySelectorAll('a[href]').forEach((a) => {
        if (!a.isConnected) return;
        const href = (a.getAttribute('href') || '').trim();
        const anchorText = (a.textContent || '').trim();

        const prev = a.previousSibling;
        const prevText =
            prev && prev.nodeType === Node.TEXT_NODE ? prev.textContent || '' : '';
        const hasProtocolPrefix =
            !!prevText && PROTOCOL_PREFIX_RE.test(prevText.trim());
        const visible = (hasProtocolPrefix ? prevText.trim() : '') + anchorText;

        // Only URL-as-text links are special; descriptive links keep their href.
        if (!FULL_URL_RE.test(visible)) return;

        const doc = a.ownerDocument;
        const block = a.parentElement;
        const prevBlock = block && block.previousElementSibling;
        const isLoneSplit =
            hasProtocolPrefix &&
            block &&
            block.tagName === 'P' &&
            prevBlock &&
            prevBlock.tagName === 'P' &&
            isLoneSplitUrlBlock(block, a, prev);

        // Self-link → plain text (view re-linkifies it). No <a> left to mangle.
        if (!href || href === visible) {
            if (hasProtocolPrefix) prev.remove();
            const textNode = doc.createTextNode(visible);
            if (isLoneSplit) {
                rejoinOntoPrevLine(prevBlock, textNode);
                block.remove();
            } else {
                a.replaceWith(textNode);
            }
            return;
        }

        // Custom target → keep the link + its href. Reassemble + rejoin only.
        if (hasProtocolPrefix) {
            a.textContent = visible;
            prev.remove();
        }
        if (isLoneSplit) {
            rejoinOntoPrevLine(prevBlock, a);
            if (!(block.textContent || '').trim()) block.remove();
        }
    });
}

// Wrap raw URLs found in text (not already inside an <a>) into clickable links.
function linkifyBareUrls(doc, body) {
    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        if (node.parentElement && node.parentElement.closest('a')) continue;
        URL_RE.lastIndex = 0;
        if (URL_RE.test(node.textContent || '')) textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
        const text = textNode.textContent || '';
        const frag = doc.createDocumentFragment();
        let lastIndex = 0;
        let match;
        URL_RE.lastIndex = 0;
        while ((match = URL_RE.exec(text))) {
            const url = match[0];
            if (match.index > lastIndex) {
                frag.appendChild(
                    doc.createTextNode(text.slice(lastIndex, match.index))
                );
            }
            const a = doc.createElement('a');
            a.setAttribute('href', url);
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
            a.textContent = url;
            frag.appendChild(a);
            lastIndex = match.index + url.length;
        }
        if (lastIndex < text.length) {
            frag.appendChild(doc.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode.replaceChild(frag, textNode);
    });
}

export function normalizeForEditor(html) {
    const ctx = parse(html);
    // relaxNbsp chạy trên chuỗi đã serialize: innerHTML mã hoá ngược U+00A0
    // thành &nbsp; nên sửa ở tầng DOM sẽ bị ghi đè.
    if (!ctx) return relaxNbsp(html);
    unwrapUrlLinks(ctx.body);
    return relaxNbsp(ctx.body.innerHTML);
}

export function normalizeForView(html) {
    const ctx = parse(html);
    if (!ctx) return relaxNbsp(html);
    // Repair any legacy split links, then linkify the remaining plain URLs.
    unwrapUrlLinks(ctx.body);
    linkifyBareUrls(ctx.doc, ctx.body);
    return relaxNbsp(ctx.body.innerHTML);
}
